import { gt } from 'common/PatternMatchingUtilities';
import { Slugs } from 'common/urls/Slugs';
import React, { useCallback } from 'react';
import { useRouteMatch } from 'react-router';
import { useFilterQueryParams } from 'src/shared/design-system/DishFirst/DishModal/utils';
import { trackDishCardClicked } from 'src/shared/design-system/DishFirstCard';
import { useHistory } from 'src/shared/hooks/useHistory';
import { useTracker } from 'src/shared/hooks/useTracker';
import { getDishCardUrlParams, getPathName } from 'src/shared/utils/DishCardUtils';
import { getSlug } from 'src/shared/utils/RouteUtilities';
import { match } from 'ts-pattern';
import { useScreenContext } from 'web-common/src/shared/context/ScreenContext/hook';
import { ScreenWidth, ScreenWidthValues } from 'web-common/src/shared/styles';
import { DishCard, DishCardHalfLandscape, DishCardLandscape } from './styles';
import { ExploreDishCardProps } from './types';
import { useQuickAddToCart } from './useQuickAddToCart';

export const ExploreDishCard: React.FC<ExploreDishCardProps> = (props) => {
  const {
    className,
    deliveryDate,
    idx,
    isFluid,
    isShefAvailable = true,
    foodItemPreview,
    lineItemAvailabilities = {},
    nextDeliveryDate = null,
    segment,
    segmentMetrics,
    scrollPosition,
    showDescription,
    location,
    onClick,
  } = props;
  const { foodItemId, slug, shef } = foodItemPreview;
  const history = useHistory();

  const {
    windowDimensions: { width },
  } = useScreenContext();

  const routeMatch = useRouteMatch();
  const zipCode = getSlug(routeMatch, Slugs.ZIP_CODE);

  const cartLineItem = lineItemAvailabilities[foodItemId];
  const quantity = cartLineItem?.quantity ?? 0;
  const numAvailable = cartLineItem?.numAvailable;

  const { increment, decrement, loading, error, maxQuantity } = useQuickAddToCart({
    deliveryDate,
    foodItemPreview,
    zipCode,
    segmentMetrics,
    idx,
    location,
    quantity,
    numAvailable,
  });
  const tracker = useTracker();
  const { filtersQueryParams } = useFilterQueryParams();

  const handleClick = useCallback(() => {
    if (segmentMetrics) {
      trackDishCardClicked(tracker, segmentMetrics, 'dish', filtersQueryParams, idx);
    }
    onClick({ foodItemSlug: slug, shefId: shef.id, segmentMetrics, idx });
  }, [onClick, slug, shef.id, segmentMetrics, idx]);

  const cardProps = {
    className,
    foodItemPreview,
    segment,
    scrollPosition,
    isAvailable: maxQuantity > 0,
    href: getPathName(
      history.location.pathname,
      getDishCardUrlParams({
        initialParams: history.location.search,
        foodItemSlug: slug,
        shefId: shef.id,
        segmentMetrics,
        idx,
      })
    ),
    increment,
    decrement,
    isFluid,
    isShefAvailable,
    lineItemAvailabilities,
    nextDeliveryDate,
    onClick: handleClick,
    quantity,
    quickAddLoading: loading,
    quickAddError: error,
    maxQuantity,
    showDescription,
  };

  return (
    match({ isResponsive: props.isResponsive, width })
      .with({ isResponsive: false }, () => <DishCard {...cardProps} />)
      // Full screen uses portrait cards
      .with({ width: gt(ScreenWidthValues[ScreenWidth.MEDIUM]) }, () => <DishCard {...cardProps} />)
      // Intermediate type for 2 columns of landscape cards
      .with({ width: gt(ScreenWidthValues[ScreenWidth.SMALL]) }, () => <DishCardHalfLandscape {...cardProps} />)
      // Landscape card for full width of mobile screens
      .otherwise(() => <DishCardLandscape {...cardProps} />)
  );
};
